import {useTranslation} from 'react-i18next';
import {SVG} from '../../../../../constants/assetLists';
import AppButton from '../../../../../components/common/AppButton';
import AppTooltip from '../../../../../components/common/AppTooltip';
import {useFormsContext} from '../../../../../context/forms/FormsContextProvider';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {useRef, useState} from 'react';
import FormShareDialog from './FormShareDialog';
import {QRCodeCanvas} from 'qrcode.react';

type Props = {
  title: string;
  onClose: () => void;
  onSaveDraft: () => void;
  onPublish: () => void;
  isSubmitting?: boolean;
  copyMode?: boolean;
};

function FormSecondaryHeader({
  title,
  onClose,
  onSaveDraft,
  onPublish,
  isSubmitting = false,
  copyMode = false,
}: Props) {
  const {t} = useTranslation();
  const {formData} = useFormsContext();
  const {showSnackbar} = useSnackbar();
  const isPublishedForm = !copyMode && formData?.status === 'published';

  const [showShareFormDialog, setShowShareFormDialog] = useState(false);

  const qrRef = useRef<HTMLCanvasElement>(null);
  const formUrl = `${window.location.origin}/form/${formData?._id}`;

  const handleQRClick = () => {
    const canvas = qrRef.current;
    if (!canvas) return;

    const qrDataUrl = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>${formData?.title || 'QR Code'}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
              direction: rtl;
            }
            h2 { margin-bottom: 24px; font-size: 24px; }
            img { width: 300px; height: 300px; }
            p { margin-top: 16px; color: #555; font-size: 14px; word-break: break-all; }
          </style>
        </head>
        <body>
          <h2>${formData?.title || ''}</h2>
          <img src="${qrDataUrl}" alt="QR Code" />
          <p>${formUrl}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleShareClick = () => {
    setShowShareFormDialog(true);
  };

  const handleCopyClick = async () => {
    // TODO: Implement copy link functionality
    if (isPublishedForm) {
      const link = `${window.location.host}/form/${formData?._id}`;
      await navigator.clipboard.writeText(link);
      showSnackbar({
        type: 'info',
        message: 'קישור הטופס הועתק בהצלחה ללוח',
      });
    }
  };

  return (
    <>
      <div className="w-full h-[56px] bg-white flex justify-between items-center px-4 shadow-sm">
        {/* Left Section - Form Title */}
        <div className="flex items-center gap-2 min-w-0 flex-1 mr-4">
          <img
            src={SVG.darkStarIcon}
            alt="star"
            className="w-[20px] h-[20px] object-contain flex-shrink-0"
          />
          <span className="text-[32px] text-black font-medium truncate">
            {title || t('forms.dialog.formTitlePlaceholder')}
          </span>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* QR Code Icon */}
          <AppTooltip
            backgroundColor="#fff"
            title={
              isPublishedForm ? (
                <div className="flex flex-col gap-1 p-1">
                  <p className="m-0 text-[16px] font-medium">
                    {t('forms.dialog.tooltip.qr.line1')}
                  </p>
                  <p className="m-0 text-[16px] font-medium">
                    {t('forms.dialog.tooltip.qr.line2')}
                  </p>
                  <div className="w-full h-[1px] bg-tealBlue my-1" />
                  <p className="m-0 text-[16px] font-normal">
                    {t('forms.dialog.tooltip.qr.line3')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-1 p-1">
                  <p className="m-0 text-[16px] font-medium">
                    {t('forms.dialog.tooltip.qr.notPublished')}
                  </p>
                  
                </div>
              )
            }>
            <button
              type="button"
              onClick={handleQRClick}
              disabled={!isPublishedForm}
              className={`p-2 ${!isPublishedForm && 'cursor-not-allowed'}`}>
              <img
                src={SVG.QRDarkIcon}
                alt="QR"
                className="w-[18px] h-[18px] object-contain"
              />
            </button>
          </AppTooltip>

          {/* Share Icon */}
          <AppTooltip
            backgroundColor="#fff"
            title={
              <div className="flex flex-col gap-1 p-1">
                <p className="m-0 text-[16px] font-medium">
                  {t('forms.dialog.tooltip.share.line1')}
                </p>
                <div className="w-full h-[1px] bg-tealBlue my-1" />
                <p className="m-0 text-[16px]">
                  {t('forms.dialog.tooltip.share.line2')}
                </p>
              </div>
            }>
            <button
              type="button"
              disabled={!isPublishedForm}
              onClick={handleShareClick}
              className={`p-2 ${!isPublishedForm && 'cursor-not-allowed'}`}>
              <img
                src={SVG.shareDarkIcon}
                alt="Share"
                className="w-[18px] h-[20px] object-contain"
              />
            </button>
          </AppTooltip>

          {/* Copy Link Icon */}
          <AppTooltip
            backgroundColor="#fff"
            title={
              <div className="flex flex-col gap-1 p-1">
                <p className="m-0 text-[16px] font-medium]">
                  {t('forms.dialog.tooltip.copy.line1')}
                </p>
                <div className="w-full h-[1px] bg-tealBlue my-1" />
                <p className="m-0 text-[16px]">
                  {t('forms.dialog.tooltip.copy.line2')}
                </p>
              </div>
            }>
            <button
              type="button"
              onClick={handleCopyClick}
              disabled={!isPublishedForm}
              className={`p-2 ${!isPublishedForm && 'cursor-not-allowed'}`}>
              <img
                src={SVG.linkBlackIcon}
                alt="Copy"
                className="w-[20px] h-[10px] object-contain"
              />
            </button>
          </AppTooltip>

          {/* Save Draft Button */}
          <AppButton
            variant="secondary"
            py="8px"
            disabled={isSubmitting}
            iconStart={
              <img
                src={SVG.tickIcon}
                className="w-[12px] h-[10px]"
                alt="tick"
              />
            }
            onClick={onSaveDraft}>
            {copyMode ? 'העתקה כטיוטה' : t('forms.dialog.saveDraft')}
          </AppButton>

          {/* Publish Button */}
          <AppButton
            variant="custom"
            py="8px"
            className="bg-linkBlue text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            disabled={isSubmitting}
            onClick={onPublish}>
            {copyMode ? 'העתקה ופרסום' : t('forms.dialog.publish')}
          </AppButton>

          {/* Close Button */}
          <button type="button" onClick={onClose} className="p-2">
            <img
              src={SVG.closeDarkIcon}
              alt="Close"
              className="w-[14px] h-[14px] object-contain"
            />
          </button>
        </div>
      </div>

      <FormShareDialog
        open={showShareFormDialog}
        onClose={() => setShowShareFormDialog(false)}
        formData={formData}
      />

      {/* Hidden QR canvas used for print */}
      <div style={{overflow: 'hidden', width: 0, height: 0}}>
        <QRCodeCanvas ref={qrRef} value={formUrl} size={300} />
      </div>
    </>
  );
}

export default FormSecondaryHeader;
